<script setup lang="ts">
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const root = ref<HTMLElement | null>(null)

const values = [
  {
    number: '01',
    titleKey: 'page.features.core_values.value_1_title',
    descKey: 'page.features.core_values.value_1_desc',
    iconColor: 'text-heart-purple',
    shadow: 'shadow-[8px_8px_0_#CB2097]',
    iconShadow: 'shadow-[3px_3px_0_#CB2097]',
    icon: 'shield',
  },
  {
    number: '02',
    titleKey: 'page.features.core_values.value_2_title',
    descKey: 'page.features.core_values.value_2_desc',
    iconColor: 'text-[#B8860B]',
    shadow: 'shadow-[8px_8px_0_#FDC02D]',
    iconShadow: 'shadow-[3px_3px_0_#FDC02D]',
    icon: 'eye',
  },
  {
    number: '03',
    titleKey: 'page.features.core_values.value_3_title',
    descKey: 'page.features.core_values.value_3_desc',
    iconColor: 'text-sky-blue',
    shadow: 'shadow-[8px_8px_0_#2E83FB]',
    iconShadow: 'shadow-[3px_3px_0_#2E83FB]',
    icon: 'sparkles',
  },
]

onMounted(() => {
  const el = root.value
  if (!el) return

  gsap.fromTo(el.querySelector('.cv-header'),
    { opacity: 0, y: 40 },
    {
      opacity: 1, y: 0, duration: 0.75, ease: 'power3.out',
      scrollTrigger: { trigger: el, start: 'top 80%', once: true },
    },
  )

  gsap.fromTo(el.querySelectorAll('.cv-card'),
    { opacity: 0, y: 60, scale: 0.94 },
    {
      opacity: 1, y: 0, scale: 1, duration: 0.7, ease: 'back.out(1.4)', stagger: 0.14,
      scrollTrigger: { trigger: el, start: 'top 75%', once: true },
    },
  )
})
</script>

<template>
  <section ref="root" class="py-20 md:py-24 lg:py-28 bg-amber-50">
    <div class="container">

      <!-- Header -->
      <div class="cv-header text-center mb-14 md:mb-16">
        <h2 class="text-[clamp(2rem,4vw,3rem)] font-black text-deep-indigo leading-tight mb-4">
          {{ $t('page.features.core_values.title') }}
        </h2>
        <p class="text-base md:text-lg text-gray-500 max-w-md mx-auto leading-relaxed">
          {{ $t('page.features.core_values.subtitle') }}
        </p>
      </div>

      <!-- Cards grid -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-7 lg:gap-8">
        <div
          v-for="(val, i) in values"
          :key="i"
          :class="['cv-card relative bg-white rounded-2xl p-7 overflow-hidden border-2 border-gray-900 transition-transform duration-150 hover:-translate-x-[3px] hover:-translate-y-[3px]', val.shadow]">

          <!-- Faded number -->
          <span class="absolute top-4 end-5 text-[6rem] font-black leading-none text-gray-100 select-none pointer-events-none">
            {{ val.number }}
          </span>

          <!-- Icon box -->
          <div :class="['relative z-10 w-14 h-14 rounded-xl border-2 border-gray-900 bg-white flex items-center justify-center mb-6', val.iconShadow]">
            <!-- Shield (Security & Protection) -->
            <svg v-if="val.icon === 'shield'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" :class="['size-7', val.iconColor]">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
              <path d="m9 12 2 2 4-4" />
            </svg>

            <!-- Eye (Transparency & Privacy) -->
            <svg v-else-if="val.icon === 'eye'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" :class="['size-7', val.iconColor]">
              <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
              <circle cx="12" cy="12" r="3" />
            </svg>

            <!-- Sparkles (Ease of Use) -->
            <svg v-else-if="val.icon === 'sparkles'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" :class="['size-7', val.iconColor]">
              <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
              <path d="M20 3v4" />
              <path d="M22 5h-4" />
              <path d="M4 17v2" />
              <path d="M5 18H3" />
            </svg>
          </div>

          <!-- Text -->
          <div class="relative z-10">
            <h3 class="text-lg font-black text-gray-900 mb-2">
              {{ $t(val.titleKey) }}
            </h3>
            <p class="text-sm text-gray-500 leading-relaxed">
              {{ $t(val.descKey) }}
            </p>
          </div>
        </div>
      </div>

    </div>
  </section>
</template>
